import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { STUDY_QUERY_KEY } from '@core/hooks/useStudyInfo';
import { QUERY_KEY as STUDIES_QUERY_KEY } from 'app/features/studies/hooks/useStudies';
import overridePatient, { OverridePatientPayload } from '../api/overridePatient';
import updateStudy from '../api/updateStudy';
import { GET_PATIENT_KEY } from './useGetPatient';
import { GET_PATIENT_STUDIES_KEY } from './useGetPatientStudies';

const useOverridePatient = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: override, isLoading: isOverriding, ...rest } = useMutation({
    mutationFn: async (payload: OverridePatientPayload) => {
      const response = await overridePatient(payload);
      // The api layer resolves HTTP errors instead of throwing; surface them so
      // the DICOM update never runs for a merge that was not saved.
      if (!response?.success) {
        throw new Error(response?.error || 'No se pudo vincular el paciente');
      }

      const { result } = response;

      // Every study now under the target patient gets its DICOM rewritten,
      // including the PatientID tag.
      const isDicomUpdated = await updateStudy(result.affectedStudyInstanceUids, {
        patientId: result.patientId,
        patientsGivenName: payload.patientsNameGiven,
        patientsSurname: payload.patientsNameFamily,
        patientsBirthDate: payload.patientsBirthDate ? dayjs(payload.patientsBirthDate) : null,
        patientsAge: result.patientsAge || payload.patientsAge,
        patientsGender: result.patientsSex || payload.patientsSex,
      });

      return { patient: result, isDicomUpdated };
    },
    onSuccess: () => {
      queryClient.invalidateQueries([STUDY_QUERY_KEY]);
      queryClient.invalidateQueries([STUDIES_QUERY_KEY]);
      queryClient.invalidateQueries([GET_PATIENT_STUDIES_KEY]);
      queryClient.removeQueries([GET_PATIENT_KEY]);
    },
  });

  return { override, isOverriding, ...rest };
};

export default useOverridePatient;
